import { Box, Flex, Img, Text } from '@chakra-ui/react'
import { useComplexQuery } from '@sequencehq/api'
import {
  Badge,
  Button,
  SequenceKebabMenu,
  Skeleton,
  StandardLayout
} from '@sequencehq/core-components'
import {
  GreyGrey60,
  Lato14Bold,
  Lato14Regular
} from '@sequencehq/design-tokens'
import { toBillingScheduleStatusBadgeProps } from '@sequencehq/utils'
import {
  fetchBillingScheduleAggregate,
  getBillingScheduleAggregateKey
} from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { ArchiveScheduleMenuItem } from 'common/menuItems/schedules/ArchiveScheduleMenuItem'
import { DuplicateScheduleMenuItem } from 'common/menuItems/schedules/DuplicateScheduleMenuItem'
import { getLogoSrc } from 'Customer/index'
import { Link, useNavigate } from 'react-router-dom'
import { EditScheduleShortcutsModal } from './BillingScheduleHomeContent/modals/editScheduleShortcuts/EditScheduleShortcutsModal'
import Spinner from 'components/Loading'

const useBillingScheduleHomeHeader = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useComplexQuery({
    queryFn: () => fetchBillingScheduleAggregate(billingScheduleId),
    queryKey: getBillingScheduleAggregateKey(billingScheduleId),
    staleTime: 60000,
    select: data => {
      return {
        status: data.billingSchedule.status,
        customerId: data.billingSchedule.customerId,
        customerDomain: data.customer?.domain,
        archivedAt: data.billingSchedule.archivedAt
      }
    }
  })

  const isArchived = Boolean(billingScheduleAggregateQuery.data?.archivedAt)

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isRefreshing: billingScheduleAggregateQuery.isFetching,
    isArchived,
    billingScheduleId,
    billingScheduleStatus: billingScheduleAggregateQuery.data?.status,
    canArchive: !billingScheduleAggregateQuery.data?.archivedAt,
    canUpdate:
      !isArchived &&
      billingScheduleAggregateQuery.data?.status &&
      ['DRAFT', 'ACTIVE', 'PENDING'].includes(
        billingScheduleAggregateQuery.data?.status
      ),
    customerLogoSrc: getLogoSrc({
      domain: billingScheduleAggregateQuery.data?.customerDomain
    }),
    customerId: billingScheduleAggregateQuery.data?.customerId
  }
}

export const BillingScheduleHomeHeader = () => {
  const {
    billingScheduleId,
    billingScheduleStatus,
    customerLogoSrc,
    isLoading,
    isRefreshing,
    canArchive,
    isArchived,
    canUpdate
  } = useBillingScheduleHomeHeader()
  const navigate = useNavigate()

  return (
    <StandardLayout.Header
      title={
        isLoading ? (
          <Skeleton height="16px" width="280px" />
        ) : (
          <Flex
            {...Lato14Regular}
            color={GreyGrey60}
            isTruncated
            display="flex"
            alignItems="center"
            gap="8px"
          >
            {customerLogoSrc && (
              <Img src={customerLogoSrc} height="16px" width="16px" />
            )}
            <Text
              as="span"
              maxWidth="300px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              display="inline-block"
              {...Lato14Bold}
            >
              <Link to="/billing-schedules">
                <Box
                  as="span"
                  {...Lato14Bold}
                  color={GreyGrey60}
                  marginRight="2px"
                >
                  Schedules
                </Box>
              </Link>{' '}
              /{' '}
              <Box as="span" marginLeft="2px">
                Billing schedule
              </Box>
            </Text>
            {billingScheduleStatus && (
              <Badge
                {...toBillingScheduleStatusBadgeProps({
                  status: billingScheduleStatus
                })}
              />
            )}
            {isArchived && (
              <Badge sentiment="neutral" size="md">
                Archived
              </Badge>
            )}
            {isRefreshing && (
              <Spinner height="16px" width="16px" color={GreyGrey60} />
            )}
          </Flex>
        )
      }
      actions={
        <Flex gap="8px" alignItems="center">
          {canUpdate && (
            <EditScheduleShortcutsModal
              trigger={<Button variant="primary">Edit schedule</Button>}
            ></EditScheduleShortcutsModal>
          )}
          <SequenceKebabMenu
            data-testid={`schedule.${billingScheduleId}.menu`}
            buttonSize="medium"
            alignment="bottom-right"
            variant="ghost"
            items={[
              <DuplicateScheduleMenuItem
                key="duplicate-schedule"
                scheduleId={billingScheduleId}
                data-testid={`schedule.${billingScheduleId}.menuItem.duplicate`}
                onSuccess={(newScheduleId: string) =>
                  navigate(`/billing-schedules/${newScheduleId}`)
                }
              />,
              ({ setIsOpen }) =>
                canArchive && (
                  <ArchiveScheduleMenuItem
                    key="archive-schedule"
                    data-testid={`schedule.${billingScheduleId}.menuItem.archive`}
                    scheduleId={billingScheduleId}
                    onSuccess={() => {
                      setIsOpen?.(false)
                    }}
                  />
                )
            ]}
          />
        </Flex>
      }
    />
  )
}
